import { useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import { Box, DialogContent, IconButton, Tab, Tabs } from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { IConfigurationDetail, IView } from '../../../types';
import { useDispatch, useSelector } from 'react-redux';
import { getConfigurationData, setSearchValue } from '../../store/slice/ConfigurationSlice';
import { AppDispatch, AppState, AppStore } from '../../store';
import { createConfigureRequest } from '../../services/ConfigurationDataHelperFunctions';

/**
 * This function returns the view list to show in the left panel
 * @returns {IView[]} returns view list
 */
function getViews(): IView[] {
  const configuration = AppStore.getState().configuration;
  const currentConfiguration = configuration.configurations.find(
    (config: IConfigurationDetail) => config.configurationId === configuration.configurationId
  );
  const views: IView[] = [];
  const names = new Set<string>();
  currentConfiguration?.views?.forEach((view: string) => {
    const name = view.split('_')[1];
    if (!names.has(name)) {
      names.add(name);
      views.push({
        id: view,
        name: name,
      });
    }
  });

  return views;
}

export function TabDrawer() {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const [openDialog, setOpenDialog] = useState(false);
  const [value, setValue] = useState(0);
  const [newValue, setNewValue] = useState(0);
  const [expand, setExpand] = useState(true);

  const { configurations, configurationId } = useSelector((state: AppState) => state.configuration);

  const onViewSelection = (index: number) => {
    if (index !== value) {
      setNewValue(index);
      setOpenDialog(true);
    }
  };

  const toggleDrawer = () => {
    setExpand(!expand);
  };

  const handleClose = async (confirmation: boolean) => {
    setOpenDialog(false);
    if (confirmation) {
      dispatch(setSearchValue(''));
      setValue(newValue);
      const configuration = configurations.find(
        (config: IConfigurationDetail) => config.configurationId === configurationId
      );

      const req = createConfigureRequest(
        { ...configuration?.configureRequest, configurationId: configurationId },
        itemsList[newValue].name,
        configuration?.productId
      );
      dispatch(getConfigurationData(req));
    }
  };

  const itemsList = getViews();

  return (
    <>
      <Dialog
        className="common-dialog-style"
        open={openDialog}
        onClose={() => handleClose(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        data-testid="dialogBox"
      >
        <DialogTitle
          className="header title"
          id="alert-dialog-title"
        >{`${t('label.confirm')} ${t('label.view')}`}</DialogTitle>

        <DialogContent className="dialog-content" id="alert-dialog-title">
          {t('message.viewChange', { view: itemsList[newValue]?.name })}
        </DialogContent>

        <DialogActions>
          <Button data-testid="confirm-view" size="large" onClick={() => handleClose(false)}>
            {t('label.no')}
          </Button>
          <Button
            data-testid="confirm-view"
            variant="contained"
            size="large"
            onClick={() => handleClose(true)}
            autoFocus
          >
            {t('label.yes')}
          </Button>
        </DialogActions>
      </Dialog>

      <Box>
        <Drawer
          variant="permanent"
          id="sideNavpanel"
          className={expand ? 'view-drawer' : 'view-drawer-collapse'}
        >
          {expand ? (
            <>
              <Box className="d-flex p-5">
                <span className="view-drawer-header">{t('label.views')}</span>
                <IconButton onClick={() => toggleDrawer()}>
                  <KeyboardDoubleArrowLeftIcon style={{ color: '1474A4' }} />
                </IconButton>
              </Box>

              <Tabs
                key="view-tabs"
                value={value}
                orientation="vertical"
                variant="scrollable"
                visibleScrollbar
              >
                {itemsList.map((item, index) => {
                  return (
                    <Tab
                      data-testid="view-tab"
                      key={item.id}
                      label={item.name}
                      className="tab-drawer-tabs"
                      iconPosition="end"
                      onClick={() => onViewSelection(index)}
                      value={index}
                    />
                  );
                })}
              </Tabs>
            </>
          ) : (
            <Box className="view-drawer-collapse-content">
              <IconButton onClick={() => toggleDrawer()}>
                <KeyboardDoubleArrowRightIcon style={{ color: '1474A4' }} />
              </IconButton>
              <Box className="view-drawer-header-collapse ">
                <span className="view-drawer-header-text ">{t('label.views')}</span>
              </Box>
            </Box>
          )}
        </Drawer>
      </Box>
    </>
  );
}
